import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { Card, Row, Col, Badge, Button } from 'react-bootstrap';
import {
    Lock,
    ArrowRight,
    CheckCircle,
    XCircle,
    Shield,
    Users,
    HardDrive,
    Bot,
    ArrowLeft,
} from 'lucide-react';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { useFeatures } from '../../context/FeatureContext';
import PageTitle from '../layouts/PageTitle';

const FEATURE_DEFINITIONS = {
    bookkeeping: { name: 'Bokföring', icon: '📒', description: 'Grundläggande bokföring och huvudbok' },
    invoicing: { name: 'Fakturering', icon: '📄', description: 'Skapa och skicka kundfakturor' },
    invoicing_quotes: { name: 'Offerter', icon: '📋', description: 'Skapa offerter och konvertera till fakturor' },
    supplier_invoices: { name: 'Leverantörsfakturor', icon: '📥', description: 'Hantera inköpsfakturor' },
    payroll: { name: 'Lönehantering', icon: '💰', description: 'Löneberäkning och utbetalning' },
    personell_management: { name: 'Personalhantering', icon: '👥', description: 'Hantera anställda och avdelningar' },
    banking_integration: { name: 'Bankintegration', icon: '🏦', description: 'Automatisk import av banktransaktioner' },
    multi_currency: { name: 'Flera valutor', icon: '💱', description: 'Bokföring i flera valutor' },
    api_access: { name: 'API-åtkomst', icon: '🔌', description: 'Programmatisk åtkomst via REST API' },
    document_management: { name: 'Dokumenthantering', icon: '📁', description: 'Avancerad dokumentlagring' },
    time_tracking: { name: 'Tidrapportering', icon: '⏱️', description: 'Tidsredovisning och projekt' },
    dashboard_widget: { name: 'Dashboard', icon: '📊', description: 'Översiktspanel och rapporter' },
    ai_assistant: { name: 'AI-assistent', icon: '🤖', description: 'Intelligent bokföringshjälp' },
    ai_ocr: { name: 'AI OCR', icon: '👁️', description: 'Automatisk fakturatolkning' },
    ai_categorization: { name: 'AI-kategorisering', icon: '🏷️', description: 'Smart kontokodning' },
    ai_reconciliation: { name: 'AI-avstämning', icon: '✅', description: 'Automatisk bankavstämning' },
    ai_invoice_agent: { name: 'AI-fakturaagent', icon: '📨', description: 'Automatisk hantering av leverantörsfakturor med Finn' },
};

const FeatureDisabledPage = () => {
    const location = useLocation();
    const history = useHistory();
    const { companyId } = useParams();
    const { entitlements } = useFeatures();

    const featureKey = location.state?.feature;
    const featureDef = FEATURE_DEFINITIONS[featureKey] || { name: featureKey, icon: '🔒', description: '' };

    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchPlans = useCallback(async () => {
        try {
            const { data } = await axios.get(getApiUrl('plans'), { withCredentials: true });
            setPlans(data || []);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const currentPlanName = entitlements?.plan?.name;

    // Find plans that include this feature, excluding the current plan
    const plansWithFeature = plans
        .filter((plan) => {
            const features = typeof plan.features === 'string'
                ? JSON.parse(plan.features)
                : plan.features || {};
            return features[featureKey] === true && plan.plan_name !== currentPlanName;
        })
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    return (
        <>
            <PageTitle
                activeMenu="Funktion ej tillgänglig"
                motherMenu="Hem"
                pageContent="Funktion ej tillgänglig"
            />

            <Row className="justify-content-center">
                <Col lg={8}>
                    {/* Main message card */}
                    <Card className="mb-4">
                        <Card.Body className="text-center py-5">
                            <div className="mb-4">
                                <div
                                    className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                                    style={{ width: '80px', height: '80px' }}
                                >
                                    <Lock size={36} className="text-muted" />
                                </div>
                            </div>

                            <h2 className="mb-2">
                                <span className="me-2" style={{ fontSize: '1.5rem' }}>{featureDef.icon}</span>
                                {featureDef.name}
                            </h2>
                            <p className="text-muted mb-4" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                {featureDef.description
                                    ? `${featureDef.description}. Denna funktion ingår inte i din nuvarande plan.`
                                    : 'Denna funktion ingår inte i din nuvarande plan.'}
                            </p>

                            <div className="d-inline-flex align-items-center bg-light rounded px-3 py-2">
                                <Shield size={16} className="me-2 text-primary" />
                                <span className="text-muted me-1">Din plan:</span>
                                <strong>{entitlements?.plan?.displayName || 'Ingen plan'}</strong>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Upgrade options */}
                    {isLoading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Laddar...</span>
                            </div>
                        </div>
                    ) : plansWithFeature.length > 0 ? (
                        <>
                            <h5 className="mb-3">
                                Tillgänglig i följande planer
                            </h5>
                            <Row>
                                {plansWithFeature.map((plan) => {
                                    const features = typeof plan.features === 'string'
                                        ? JSON.parse(plan.features)
                                        : plan.features || {};
                                    const enabledFeatures = Object.entries(features).filter(([, v]) => v === true);

                                    return (
                                        <Col md={6} key={plan.plan_id} className="mb-4">
                                            <Card className="h-100">
                                                <Card.Body className="d-flex flex-column">
                                                    <h4 className="text-center mb-3">{plan.display_name}</h4>

                                                    <div className="text-center mb-3">
                                                        <h2 className="mb-0">
                                                            {plan.price_monthly_sek > 0
                                                                ? `${plan.price_monthly_sek} kr`
                                                                : 'Gratis'}
                                                        </h2>
                                                        {plan.price_monthly_sek > 0 && (
                                                            <small className="text-muted">/månad</small>
                                                        )}
                                                    </div>

                                                    <hr />

                                                    <ul className="list-unstyled mb-3">
                                                        <li className="mb-2">
                                                            <Bot size={14} className="me-2 text-primary" />
                                                            <strong>{plan.monthly_ai_credits}</strong> AI-krediter/månad
                                                        </li>
                                                        <li className="mb-2">
                                                            <Users size={14} className="me-2 text-primary" />
                                                            Max <strong>{plan.max_users}</strong> användare
                                                        </li>
                                                        <li className="mb-2">
                                                            <HardDrive size={14} className="me-2 text-primary" />
                                                            <strong>{plan.max_storage_gb} GB</strong> lagring
                                                        </li>
                                                    </ul>

                                                    <ul className="list-unstyled flex-grow-1">
                                                        <li className="mb-2">
                                                            <small className="text-muted">Funktioner:</small>
                                                        </li>
                                                        {enabledFeatures.slice(0, 8).map(([key]) => {
                                                            const def = FEATURE_DEFINITIONS[key] || { name: key };
                                                            const isRequestedFeature = key === featureKey;
                                                            return (
                                                                <li key={key} className={`mb-1 ms-3 ${isRequestedFeature ? 'fw-bold' : ''}`}>
                                                                    <CheckCircle
                                                                        size={12}
                                                                        className={`me-1 ${isRequestedFeature ? 'text-primary' : 'text-success'}`}
                                                                    />
                                                                    <small>{def.name}</small>
                                                                    {isRequestedFeature && (
                                                                        <Badge bg="primary" className="ms-1" style={{ fontSize: '0.65rem' }}>
                                                                            Efterfrågad
                                                                        </Badge>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                        {enabledFeatures.length > 8 && (
                                                            <li className="ms-3">
                                                                <small className="text-muted">
                                                                    +{enabledFeatures.length - 8} fler...
                                                                </small>
                                                            </li>
                                                        )}
                                                    </ul>

                                                    <div className="d-grid mt-3">
                                                        <Button
                                                            variant="primary"
                                                            onClick={() => history.push(`/${companyId}/settings/subscription#planer`)}
                                                        >
                                                            Uppgradera
                                                            <ArrowRight size={16} className="ms-2" />
                                                        </Button>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    ) : (
                        <Card>
                            <Card.Body className="text-center py-4">
                                <XCircle size={24} className="text-muted mb-2" />
                                <p className="text-muted mb-0">
                                    Denna funktion är inte tillgänglig i någon plan just nu. Kontakta oss för mer information.
                                </p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Back button */}
                    <div className="mt-3 mb-5">
                        <Button
                            variant="outline-secondary"
                            onClick={() => history.push(`/${companyId}/dashboard`)}
                        >
                            <ArrowLeft size={16} className="me-2" />
                            Tillbaka till översikten
                        </Button>
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default FeatureDisabledPage;
